// Variable substitution for page text fields. Tokens like {{key}} or
// {{key.dotted.path}} are replaced. Unknown tokens are left in place so
// authors notice typos.
//
// Two call shapes:
//  - flat map:  substituteVars(text, { key: value, ... })
//  - resolver:  substituteVars(text, (key) => string | undefined)
//
// The resolver form lets the action runner expose lazy values (response body
// JSON paths, per-action loading flags) without materializing a giant map on
// every render.

import { ageOf } from '../live/time'
import { parseIdList } from '../live/ids'

// `{{key}}` or `{{key|filter}}`. Filters are a small fixed set (see
// `applyTokenFilter`); an unknown filter leaves the token as written.
const VAR_RE = /\{\{\s*([a-zA-Z0-9_.[\]]+)\s*(?:\|\s*([a-z_]+)\s*)?\}\}/g

/**
 * Token filters — generic transforms an author can put on any `{{…}}` token:
 *   age_min / age_sec  whole minutes / seconds since a date-time value
 *   count              how many entries a list holds (a selection, `a,b,c`)
 * Returns undefined for an unknown filter (the token is then left as written).
 */
export function applyTokenFilter(value: unknown, filter: string, now = Date.now()): string | undefined {
  switch (filter) {
    case 'age_min': return ageOf(value, 'min', now)
    case 'age_sec': return ageOf(value, 'sec', now)
    case 'count': return String(parseIdList(value).length)
    default: return undefined
  }
}

// Values can be strings (scalar inputs), numbers/booleans (coerced inputs),
// or arbitrary objects/arrays (`type: 'json'` variables sourced from an
// API). All non-string resolved values are stringified for substitution
// into text — objects/arrays render as compact JSON, scalars as String(v).
export type VarSource = Record<string, unknown> | ((key: string) => unknown)

export function substituteVars(text: string | undefined, vars: VarSource): string {
  if (text === null || text === undefined || text === '') return ''
  // A BOUND setting is whatever its column holds — a price is a number, a
  // flag a boolean, a date a Date. Every renderer pipes its text through
  // here, so one bound number used to throw `text.replace is not a
  // function` and, because the error escaped the renderer, took the whole
  // page's markup with it. Coerce instead: a non-string has no tokens in
  // it and is simply its own display form.
  if (typeof text !== 'string') return toDisplayString(text)
  const resolve = typeof vars === 'function'
    ? vars
    : (k: string) => (Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : undefined)
  return text.replace(VAR_RE, (match, key: string, filter?: string) => {
    // Resolver may return either the value at `key`, or — for dotted
    // tokens like `customer.email` — walk into a JSON variable's body
    // when the flat key isn't present.
    let v: unknown = resolve(key)
    if (v === undefined && key.includes('.')) {
      const dot = key.indexOf('.')
      const head = key.slice(0, dot)
      const tail = key.slice(dot + 1)
      const root = resolve(head)
      if (root !== undefined) v = getPath(root, tail)
    }
    // `|count` of a variable that has never been set is 0 (an empty
    // selection), not an unresolved token.
    if (v === undefined && filter === 'count') return '0'
    if (v === undefined) return match
    if (filter) {
      const f = applyTokenFilter(v, filter)
      return f === undefined ? match : f
    }
    return toDisplayString(v)
  })
}

// JSON-path resolver: getPath(obj, "a.b[0].c") → value or undefined.
// Supports dotted keys and `[index]` array access.
export function getPath(obj: unknown, path: string): unknown {
  if (obj == null || !path) return undefined
  // Normalize "[0]" → ".0"
  const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean)
  let cur: unknown = obj
  for (const p of parts) {
    if (cur == null) return undefined
    if (typeof cur !== 'object') return undefined
    cur = (cur as Record<string, unknown>)[p]
  }
  return cur
}

// Stringify a resolved value for substitution. Nested objects/arrays render as
// compact JSON; null/undefined render as empty string.
export function toDisplayString(v: unknown): string {
  if (v == null) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  try { return JSON.stringify(v) } catch { return String(v) }
}
